<template>
  <section class="space-y-6">
    <div class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex items-start gap-4">
          <button type="button" class="mt-1 text-xl text-muted transition hover:text-brand" @click="router.push('/dashboard')">
            <i class="ri-arrow-left-line"></i>
          </button>
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-[#b78969]">Vendor Business Desk</p>
            <h1 class="mt-2 text-[2rem] font-semibold leading-tight text-ink">{{ detail.businessName || 'Business details' }}</h1>
            <div class="mt-3 flex flex-wrap items-center gap-2 text-sm text-[#6e5a49]">
              <span class="rounded-full bg-[#fff7ef] px-3 py-1 font-semibold text-brand">
                {{ detail.entityType || 'Business vendor' }}
              </span>
              <span class="inline-flex items-center gap-2 rounded-full bg-[#faf5ef] px-3 py-1">
                <i class="ri-user-3-line text-brand"></i>
                {{ detail.ownerName || 'Owner not available' }}
              </span>
              <span class="inline-flex items-center gap-2 rounded-full bg-[#faf5ef] px-3 py-1">
                <i class="ri-bank-line text-brand"></i>
                {{ venues.length }} venues
              </span>
              <span class="inline-flex items-center gap-2 rounded-full bg-[#faf5ef] px-3 py-1">
                <i class="ri-file-shield-line text-brand"></i>
                {{ documentsUploaded }}/3 docs
              </span>
            </div>
            <p class="mt-4 max-w-3xl text-sm leading-7 text-[#5f4b3c]">
              {{ detail.description || 'Review your business profile, manage venues, and keep compliance files current from one place.' }}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-3">
          <button
            type="button"
            class="rounded-xl border border-[#e5d8cc] bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            @click="router.push('/dashboard')"
          >
            Back to dashboard
          </button>
          <button
            type="button"
            class="rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
            @click="goToEdit()"
          >
            Edit business
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white p-8 text-sm text-muted">
      Loading business details...
    </div>

    <div v-else-if="error" class="rounded-[28px] border border-red-100 bg-red-50 p-6 text-sm font-semibold text-red-700">
      {{ error }}
    </div>

    <template v-else>
      <div class="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_360px]">
        <div class="space-y-6">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Business Profile</p>
                <h2 class="mt-2 font-serif text-[2rem] font-semibold text-ink">Operational identity</h2>
              </div>
              <button type="button" class="rounded-full border border-[#e5d8cc] px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand" @click="goToEdit()">
                Update info
              </button>
            </div>

            <div class="mt-6 grid gap-4 md:grid-cols-2">
              <article v-for="item in profileCards" :key="item.label" class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">{{ item.label }}</p>
                <p class="mt-2 text-base font-semibold text-ink">{{ item.value }}</p>
                <p v-if="item.meta" class="mt-1 text-xs text-muted">{{ item.meta }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venues</p>
                <h2 class="mt-2 font-serif text-[2rem] font-semibold text-ink">Venue portfolio</h2>
              </div>
              <span class="rounded-full bg-[#f6eee5] px-3 py-1 text-xs font-semibold text-brand">{{ venues.length }}</span>
            </div>

            <div v-if="venuesLoading" class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] p-5 text-sm text-muted">
              Loading venues...
            </div>
            <div v-else-if="venues.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article
                v-for="venue in venues"
                :key="venue.id"
                class="group overflow-hidden rounded-[26px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffdfb_0%,#fff6ef_100%)] p-5 shadow-[0_10px_30px_rgba(102,73,44,0.05)] transition hover:-translate-y-0.5 hover:border-[#d9b594] hover:shadow-[0_18px_40px_rgba(102,73,44,0.12)]"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-3">
                      <span class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand shadow-sm ring-1 ring-[#eadfd4]">
                        <i class="ri-bank-line text-xl"></i>
                      </span>
                      <div class="min-w-0">
                        <p class="truncate text-[1.25rem] font-semibold text-ink">{{ venue.title }}</p>
                        <p class="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue Listing</p>
                      </div>
                    </div>
                    <p class="mt-4 flex items-start gap-2 text-sm leading-6 text-[#6f5a48]">
                      <i class="ri-map-pin-2-line mt-0.5 text-base text-brand"></i>
                      <span class="line-clamp-2">{{ venue.address || 'Address not available' }}</span>
                    </p>
                  </div> 
                </div>

                <div class="mt-5 flex flex-wrap items-center gap-2">
                  <span class="rounded-full bg-[#f7ebe1] px-3 py-1 text-xs font-semibold text-[#8c674b]">
                    {{ venue.spaces ?? 0 }} spaces
                  </span>
                  <span v-if="venue.tagLine" class="truncate rounded-full bg-[#fff3e6] px-3 py-1 text-xs font-medium text-[#a56c43]">
                    {{ venue.tagLine }}
                  </span>
                </div>

                <div class="mt-5 flex flex-wrap gap-3 border-t border-[#eadfd4]/80 pt-4">
                  <button
                    type="button"
                    class="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white transition hover:bg-brandDark"
                    @click="openVenueDetail(venue)"
                  >
                    View venue
                  </button>
                  <button
                    type="button"
                    class="rounded-full border border-[#e5d8cc] px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    @click="openVenueEditor(venue)"
                  >
                    Edit venue
                  </button>
                </div>
              </article>
            </div>
            <div v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] p-5 text-sm text-muted">
              No venues added for this business.
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Documents</p>
                <h2 class="mt-2 font-serif text-[2rem] font-semibold text-ink">Compliance files</h2>
              </div>
              <button type="button" class="rounded-full border border-[#e5d8cc] px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand" @click="goToEdit('documents')">
                Manage files
              </button>
            </div>

            <div class="mt-5 grid gap-4 md:grid-cols-3">
              <article v-for="doc in documentCards" :key="doc.key" class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">{{ doc.label }}</p>
                <a v-if="doc.url" :href="doc.url" target="_blank" rel="noreferrer" class="mt-4 block">
                  <img
                    v-if="doc.type === 'image'"
                    :src="doc.url"
                    :alt="doc.label"
                    class="h-36 w-full rounded-2xl object-cover"
                  >
                  <div v-else class="flex items-center gap-3 rounded-2xl border border-[#eadfd4] bg-white p-4">
                    <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff3e5] text-xl text-brand">
                      <i :class="doc.icon"></i>
                    </div>
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-ink">{{ doc.fileName }}</p>
                      <p class="mt-1 text-xs font-medium uppercase tracking-wide text-muted">{{ doc.extension }}</p>
                    </div>
                  </div>
                </a>
                <p v-else class="mt-4 text-sm text-muted">No document uploaded.</p>
              </article>
            </div>
          </section>
        </div>

        <aside class="space-y-6">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Contact Hub</p>
            <div class="mt-5 space-y-4">
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Phone</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ formattedPhone }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Email</p>
                <p class="mt-2 text-sm font-semibold text-ink break-all">{{ detail.email || 'Not available' }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Website</p>
                <a v-if="detail.website" :href="detail.website" target="_blank" rel="noreferrer" class="mt-2 inline-flex text-sm font-semibold text-brand hover:text-brandDark">
                  {{ detail.website }}
                </a>
                <p v-else class="mt-2 text-sm font-semibold text-ink">Not available</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-muted">Registered address</p>
                <p class="mt-2 text-sm leading-7 text-[#4b3b2f]">{{ fullAddress }}</p>
              </div>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <h3 class="font-serif text-[1.6rem] font-semibold text-ink">Quick actions</h3>
            <div class="mt-5 space-y-3">
              <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="goToEdit()">
                <span class="text-sm font-semibold text-ink">Edit full business information</span>
                <i class="ri-arrow-right-line text-brand"></i>
              </button>
              <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="goToEdit('documents')">
                <span class="text-sm font-semibold text-ink">Manage documents</span>
                <i class="ri-arrow-right-line text-brand"></i>
              </button>
              <button type="button" class="flex w-full items-center justify-between rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fff8f2]" @click="goToEdit('contact')">
                <span class="text-sm font-semibold text-ink">Update contact details</span>
                <i class="ri-arrow-right-line text-brand"></i>
              </button>
            </div>
          </section>
        </aside>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/vendor/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const venuesLoading = ref(false)
const venues = ref([])

const detail = reactive({
  businessName: '',
  ownerName: '',
  description: '',
  image: '',
  entityType: '',
  gstNumber: '',
  pan: '',
  tradeLicenseNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  pinCode: '',
  phoneCode: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  latitude: '',
  longitude: '',
  doc1: '',
  doc2: '',
  doc3: ''
})

const isValidValue = (value) => {
  if (value == null) return false
  const normalized = String(value).trim()
  return normalized !== '' && normalized !== '0'
}

const getDocumentExtension = (value) => {
  if (!isValidValue(value)) return ''
  const cleanedValue = String(value).split('?')[0].split('#')[0]
  const filename = cleanedValue.split('/').pop() || ''
  return (filename.split('.').pop() || '').toLowerCase()
}

const getDocumentPreviewType = (value) => ['jpg', 'jpeg', 'png', 'webp'].includes(getDocumentExtension(value)) ? 'image' : 'file'
const getDocumentFileName = (value) => String(value).split('?')[0].split('#')[0].split('/').pop() || 'Document'
const getDocumentIcon = (value) => {
  const extension = getDocumentExtension(value)
  if (extension === 'pdf') return 'ri-file-pdf-line'
  if (extension === 'doc' || extension === 'docx') return 'ri-file-word-line'
  if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) return 'ri-image-line'
  return 'ri-file-line'
}
const getDocumentExtensionLabel = (value) => {
  const extension = getDocumentExtension(value)
  return extension ? extension.toUpperCase() : 'FILE'
}

const documentCards = computed(() => ([
  { key: 'doc1', label: 'Trade license', url: detail.doc1 },
  { key: 'doc2', label: 'Insurance certificate', url: detail.doc2 },
  { key: 'doc3', label: 'Other certifications', url: detail.doc3 }
]).map((item) => ({
  ...item,
  type: getDocumentPreviewType(item.url),
  icon: getDocumentIcon(item.url),
  fileName: getDocumentFileName(item.url),
  extension: getDocumentExtensionLabel(item.url)
})))

const documentsUploaded = computed(() => documentCards.value.filter((item) => item.url).length)
const formattedPhone = computed(() => [detail.phoneCode, detail.phone].filter(Boolean).join(' ').trim() || 'Not available')
const fullAddress = computed(() => {
  const parts = [detail.streetAddress, detail.address, detail.city, detail.state, detail.pinCode].filter(Boolean)
  return parts.length ? parts.join(', ') : 'Not available'
})

const profileCards = computed(() => [
  { label: 'Business name', value: detail.businessName || 'Not available' },
  { label: 'Owner name', value: detail.ownerName || 'Not available' },
  { label: 'Entity type', value: detail.entityType || 'Not available' },
  { label: 'GST number', value: detail.gstNumber || 'Not available' },
  { label: 'PAN', value: detail.pan || 'Not available' },
  { label: 'Trade license', value: detail.tradeLicenseNumber || 'Not available' }
])

const normalizeVenue = (item = {}, index = 0) => ({
  id: String(item?.id || item?.venue_id || item?.vendor_business_venue_id || `venue-${index}`),
  title: item?.title || item?.name || 'Untitled venue',
  address: item?.address || item?.location || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  spaces: item?.spaces ?? item?.space_count ?? item?.total_spaces ?? 0,
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft'
})

const populate = (item = {}) => {
  detail.businessName = item.business_name || item.businessName || item.name || ''
  detail.ownerName = item.owner_name || item.ownerName || item.owner || ''
  detail.description = item.description || item.about || ''
  detail.image = item.photo || item.image || item.img || ''
  detail.entityType = item.entity_type || item.entityType || ''
  detail.gstNumber = item.gst_number || ''
  detail.pan = item.pan_card_no || ''
  detail.tradeLicenseNumber = item.trade_license_no || ''
  detail.streetAddress = item.street_address || item.streetAddress || ''
  detail.city = item.city || ''
  detail.state = item.state || ''
  detail.pinCode = item.pin_code || item.pinCode || ''
  detail.phoneCode = item.phone_code ?? item.phoneCode ?? ''
  detail.phone = item.phone ?? ''
  detail.email = item.email ?? ''
  detail.website = item.website ?? ''
  detail.address = item.address ?? item.location ?? ''
  detail.latitude = item.latitude ?? ''
  detail.longitude = item.longitude ?? ''
  detail.doc1 = isValidValue(item.doc1) ? item.doc1 : ''
  detail.doc2 = isValidValue(item.doc2) ? item.doc2 : ''
  detail.doc3 = isValidValue(item.doc3) ? item.doc3 : ''
}

const loadBusiness = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('business_id', route.params.id)
    const { data } = await api.editBusinessVendor(formData)
    const payload = Array.isArray(data?.data) ? data.data[0] : data?.data || data?.business || data?.item || null
    if (!payload) throw new Error('Business not found')
    populate(payload)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to load business details'
  } finally {
    loading.value = false
  }
}

const loadVenues = async () => {
  venuesLoading.value = true
  try {
    const formData = new FormData()
    formData.append('business_id', route.params.id)
    const { data } = await api.listVenuesBusinessVendor(formData)
    const payload = data?.data || data || []
    const items = Array.isArray(payload) ? payload : payload?.venues || payload?.items || payload?.list || payload?.venue_list || []
    venues.value = Array.isArray(items) ? items.map(normalizeVenue) : []
  } catch {
    venues.value = []
  } finally {
    venuesLoading.value = false
  }
}

const goToEdit = (tab = 'business') => {
  router.push({
    name: 'business-edit',
    params: { id: route.params.id },
    query: { tab }
  })
}

const openVenueEditor = (venue) => {
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: route.params.id,
      moduleType: 'venue',
      moduleId: venue.id
    },
    query: {
      businessName: detail.businessName || 'Business',
      section: 'overview'
    }
  })
}

const openVenueDetail = (venue) => {
  router.push({
    name: 'business-venue-detail',
    params: {
      id: route.params.id,
      venueId: venue.id
    },
    query: {
      businessName: detail.businessName || 'Business',
      venueTitle: venue.title || 'Venue detail',
      venueAddress: venue.address || ''
    }
  })
}

onMounted(() => {
  loadBusiness()
  loadVenues()
})
</script>
